/* Guscio del visualizzatore — porting da flusso-urgenze.html (buildToolbar,
   wire, renderDetail, renderDocSummary, pinGutter, scrollToY, setZoom, setPanel,
   updateLegend, select 1548-1846), adattato al modello condiviso.
   L'editing (fasi 6-7) si aggancia tramite gli hook pubblici. */
import {
  COLORMAPS,
  firstLine,
  initialState,
  parseRuns,
  prepare,
  splitRunsByLine,
  type DiagramDoc,
  type DiagramState,
  type FlowStep,
  type PEvent,
  type PreparedModel,
} from "@fullsteps/shared";
import { applyTheme, savedTheme } from "./ui/theme.js";
import { renderDiagram } from "./render/diagram.js";
import { clearMeasureCache, el } from "./render/svg.js";

type MsgRaw = Extract<FlowStep, { t: "msg" }>;

export class App {
  doc: DiagramDoc;
  model: PreparedModel;
  S: DiagramState;
  private reduced = matchMedia("(prefers-reduced-motion: reduce)");
  private scrollRAF = 0;

  /** Hook di editing: se presente e ritorna true, sostituisce il dettaglio di
      sola lettura con un editor. Ricevente: (contenitore, messaggio|null). */
  onRenderDetail: ((box: HTMLElement, msg: PEvent | null) => boolean) | null = null;
  /** Chiamato dopo ogni ridisegno (per aggiornare UI di editing dipendenti). */
  onAfterDraw: (() => void) | null = null;
  /** Chiamato alla selezione di un messaggio dal diagramma (numero passo). */
  onSelect: ((n: number) => void) | null = null;

  constructor(
    private root: HTMLElement,
    doc: DiagramDoc,
  ) {
    this.doc = doc;
    this.model = prepare(doc);
    this.S = initialState();
    this.S.theme = savedTheme();
  }

  private $(sel: string): HTMLElement {
    return this.root.querySelector(sel) as HTMLElement;
  }
  private get head(): SVGElement {
    return this.root.querySelector("#head") as SVGElement;
  }
  private get diag(): SVGElement {
    return this.root.querySelector("#diag") as SVGElement;
  }
  private get scroller(): HTMLElement {
    return this.$("#scroll");
  }

  /* ---- bootstrap ---- */
  init(): void {
    this.buildToolbar();
    this.wire();
    this.draw();
    this.renderDetail();
    this.renderDocSummary();
    document.fonts?.ready.then(() => {
      clearMeasureCache();
      this.draw();
    });
  }

  /** Sostituisce il documento (dopo un'operazione di editing o un ripristino),
      ricostruisce il modello e la toolbar e ridisegna. */
  setDoc(doc: DiagramDoc, opts: { keepDetail?: boolean } = {}): void {
    this.doc = doc;
    this.model = prepare(doc);
    if (this.S.sel != null && this.S.sel > this.model.total) this.S.sel = null;
    this.rebuildToolbar();
    this.draw();
    if (!opts.keepDetail) this.renderDetail();
    this.renderDocSummary();
  }

  /* ---- disegno ---- */
  draw(): void {
    const res = renderDiagram(this.head, this.diag, this.model, this.S, {
      onTogglePhase: (id) => {
        this.S.collapsed.has(id) ? this.S.collapsed.delete(id) : this.S.collapsed.add(id);
        this.draw();
      },
      onSelectMsg: (n) => this.select(n),
    });
    this.pinGutter();
    const counter = this.$("#counter");
    if (counter) {
      counter.textContent = res.visCount + " / " + res.total + " passi";
      counter.classList.toggle("on", res.visCount !== res.total);
    }
    this.syncButtons();
    this.onAfterDraw?.();
  }

  /* ---- pannello dettaglio ---- */
  select(n: number): void {
    this.S.sel = this.S.sel === n ? null : n;
    if (this.S.sel != null) this.onSelect?.(this.S.sel);
    if (this.S.sel) {
      const m = this.model.MSGS[this.S.sel - 1];
      if (m && m.phase) this.S.collapsed.delete(m.phase.id);
      if (this.$("#main").classList.contains("pcoll")) this.setPanel(true, false);
    }
    this.draw();
    this.renderDetail();
    if (this.S.sel) {
      const m = this.model.MSGS[this.S.sel - 1];
      if (m && m.y != null) this.scrollToY(m.y);
    }
  }

  renderDetail(): void {
    this.updateLegend();
    const box = this.$("#detail");
    box.textContent = "";
    const m = this.S.sel ? (this.model.MSGS[this.S.sel - 1] ?? null) : null;
    if (this.onRenderDetail && this.onRenderDetail(box, m)) return;
    if (!m) {
      box.appendChild(el("h2", undefined, "Dettaglio passo"));
      box.appendChild(
        el("p", "hint",
          "Clic su un messaggio del diagramma (o Tab + Invio da tastiera) per leggere testo completo, documenti citati, scadenze e fase."),
      );
      return;
    }
    const raw = m.raw as MsgRaw;
    const row = m.row!;
    box.appendChild(el("h2", undefined, "Dettaglio passo"));
    const dl = el("dl", "kv");
    const add = (k: string, node: string | Node): void => {
      const dt = el("dt", undefined, k);
      const dd = el("dd");
      if (typeof node === "string") dd.textContent = node;
      else dd.appendChild(node);
      dl.appendChild(dt);
      dl.appendChild(dd);
    };
    add("Passo", el("span", "num", "#" + m.n + (row.msgs.length > 1 ? " · gruppo contemporaneo" : "")));
    add("Mittente", this.chipFor(raw.from));
    const tw = el("div");
    for (const x of row.msgs) {
      tw.appendChild(this.chipFor((x.raw as MsgRaw).to));
      tw.appendChild(document.createTextNode(" "));
    }
    add(row.msgs.length > 1 ? "Destinatari" : "Destinatario", tw);
    add("Fase", m.phase ? "F" + m.phase.num + " · " + m.phase.title : "—");
    if (m.path.length) add("Blocco", m.path.map((p) => p.kind + " [" + p.cond + "]").join(" › "));
    if (raw.deadline) add("Scadenza", el("span", "warn", "⏱ " + raw.deadline.label));
    box.appendChild(dl);

    const tx = el("div", "msgtext");
    splitRunsByLine(parseRuns(raw.text, COLORMAPS[this.S.theme])).forEach((runs, i) => {
      if (i) tx.appendChild(document.createTextNode("\n"));
      for (const r of runs) {
        const s = el("span", undefined, r.text);
        if (r.color) s.style.color = r.color;
        tx.appendChild(s);
      }
    });
    box.appendChild(tx);

    if (row.msgs.length > 1) {
      const extra = row.msgs.filter((x) => x !== m && (x.lines?.length ?? 1) > 1);
      if (extra.length) {
        box.appendChild(el("p", "hint", "Altri messaggi della stessa riga con testo esteso:"));
        for (const x of extra) {
          const xr = x.raw as MsgRaw;
          const b = el("button", "dl-item");
          const bd = el("div", "b");
          bd.appendChild(el("div", "who", this.name(xr.from) + " → " + this.name(xr.to)));
          bd.appendChild(el("div", "tx", firstLine(xr.text) + " …"));
          b.appendChild(el("span", "n", "#" + x.n));
          b.appendChild(bd);
          b.addEventListener("click", () => {
            this.S.sel = x.n!;
            this.draw();
            this.renderDetail();
          });
          box.appendChild(b);
        }
      }
    }
    if (m.docs && m.docs.length) {
      box.appendChild(el("h2", undefined, "Documenti citati"));
      const wrap = el("div", "doclist");
      for (const id of m.docs) {
        const d = this.model.docById.get(id);
        const b = el("button", "docbtn", d?.short ?? id);
        b.title = d?.label ?? id;
        b.setAttribute("aria-pressed", String(this.S.docs.has(id)));
        b.addEventListener("click", () => {
          this.toggle(this.S.docs, id);
          this.draw();
          this.renderDetail();
          this.renderDocSummary();
        });
        wrap.appendChild(b);
      }
      box.appendChild(wrap);
    }
  }

  private name(id: string): string {
    return this.model.partById.get(id)?.name ?? id;
  }
  private chipFor(id: string): HTMLElement {
    const p = this.model.partById.get(id);
    const s = el("span", "chip");
    const d = el("span", "dot");
    d.style.background = p?.colors[this.S.theme] ?? "#888";
    s.appendChild(d);
    s.appendChild(document.createTextNode(p?.name ?? id));
    return s;
  }

  renderDocSummary(): void {
    const box = this.$("#docsum");
    box.textContent = "";
    if (!this.S.docs.size) {
      box.hidden = true;
      return;
    }
    box.hidden = false;
    box.appendChild(el("h2", undefined, "Ciclo di vita del documento"));
    for (const id of this.S.docs) {
      const d = this.model.docById.get(id);
      const h = el("p");
      h.style.margin = "2px 0 6px";
      h.appendChild(el("strong", undefined, d?.label ?? id));
      box.appendChild(h);
      const steps = this.model.MSGS.filter((m) => m.docs!.includes(id));
      box.appendChild(el("p", "hint", steps.length + " passi lo coinvolgono"));
      for (const m of steps) {
        const raw = m.raw as MsgRaw;
        const b = el("button", "dl-item");
        b.appendChild(el("span", "n", "#" + m.n));
        const bd = el("div", "b");
        bd.appendChild(el("div", "who", this.name(raw.from) + " → " + this.name(raw.to)));
        bd.appendChild(el("div", "tx", firstLine(raw.text)));
        bd.appendChild(el("div", "ph", m.phase ? "F" + m.phase.num + " · " + m.phase.title : ""));
        b.appendChild(bd);
        b.addEventListener("click", () => this.select(m.n!));
        box.appendChild(b);
      }
    }
  }

  /* ---- toolbar ---- */
  private toggle<T>(set: Set<T>, v: T): void {
    set.has(v) ? set.delete(v) : set.add(v);
  }

  private buildToolbar(): void {
    const fa = this.$("#fActors");
    for (const p of this.doc.participants) {
      const b = el("button", "btn");
      b.setAttribute("aria-pressed", "false");
      b.dataset.actor = p.id;
      const c = el("span", "chip");
      c.style.color = p.colors[this.S.theme];
      c.appendChild(el("span", "dot"));
      c.appendChild(document.createTextNode(p.name));
      b.appendChild(c);
      b.addEventListener("click", () => {
        this.toggle(this.S.actors, p.id);
        this.draw();
      });
      fa.appendChild(b);
    }
    const fp = this.$("#fPhases");
    const fj = this.$("#fJump");
    for (const p of this.model.PHASES) {
      const b = el("button", "btn");
      b.setAttribute("aria-pressed", "false");
      b.dataset.phase = p.id;
      b.textContent = "F" + p.num + " · " + p.title;
      b.addEventListener("click", () => {
        this.toggle(this.S.phases, p.id);
        this.draw();
      });
      fp.appendChild(b);
      const j = el("button", "btn sm mono", "F" + p.num);
      j.title = "Vai a: " + p.title;
      j.addEventListener("click", () => {
        if (this.S.collapsed.delete(p.id)) this.draw();
        if (p.y != null) this.scrollToY(p.y - 30);
      });
      fj.appendChild(j);
    }
    const fd = this.$("#fDocs");
    const groups = new Map<string, { g: HTMLElement; ids: string[] }>();
    for (const d of this.doc.docs) {
      const mk = (): HTMLElement => {
        const b = el("button", "btn sm");
        b.setAttribute("aria-pressed", "false");
        b.dataset.doc = d.id;
        b.textContent = d.short;
        b.title = d.label + " — " + this.model.MSGS.filter((m) => m.docs!.includes(d.id)).length + " passi";
        b.addEventListener("click", () => {
          this.toggle(this.S.docs, d.id);
          this.draw();
          this.renderDocSummary();
          this.renderDetail();
        });
        return b;
      };
      if (d.group) {
        if (!groups.has(d.group)) {
          const g = el("span", "docgroup");
          const lab = el("button", "glab", d.group);
          lab.title = "Seleziona entrambi i significati di " + d.group;
          g.appendChild(lab);
          fd.appendChild(g);
          groups.set(d.group, { g, ids: [] });
          lab.addEventListener("click", () => {
            const ids = groups.get(d.group!)!.ids;
            const all = ids.every((i) => this.S.docs.has(i));
            for (const i of ids) all ? this.S.docs.delete(i) : this.S.docs.add(i);
            this.draw();
            this.renderDocSummary();
            this.renderDetail();
          });
        }
        const rec = groups.get(d.group)!;
        rec.ids.push(d.id);
        rec.g.appendChild(mk());
      } else fd.appendChild(mk());
    }
  }

  private rebuildToolbar(): void {
    for (const id of ["#fActors", "#fPhases", "#fJump", "#fDocs"]) this.$(id).textContent = "";
    this.buildToolbar();
  }

  private syncButtons(): void {
    this.root.querySelectorAll<HTMLElement>("[data-actor]").forEach((b) =>
      b.setAttribute("aria-pressed", String(this.S.actors.has(b.dataset.actor!))),
    );
    this.root.querySelectorAll<HTMLElement>("[data-phase]").forEach((b) =>
      b.setAttribute("aria-pressed", String(this.S.phases.has(b.dataset.phase!))),
    );
    this.root.querySelectorAll<HTMLElement>("[data-doc]").forEach((b) =>
      b.setAttribute("aria-pressed", String(this.S.docs.has(b.dataset.doc!))),
    );
    this.$("#mDim").setAttribute("aria-pressed", String(this.S.mode === "dim"));
    this.$("#mHide").setAttribute("aria-pressed", String(this.S.mode === "hide"));
    this.$("#zRst").textContent = Math.round(this.S.zoom * 100) + "%";
    const allClosed = this.S.collapsed.size === this.model.PHASES.length;
    this.$("#foldAll").textContent = allClosed ? "Apri tutte" : "Chiudi tutte";
  }

  /* ---- scorrimento / zoom / pannello ---- */
  private pinGutter(): void {
    const dx = this.scroller.scrollLeft / this.S.zoom;
    const g = this.diag.querySelector("#gutter");
    const h = this.head.querySelector("#gutterhead");
    if (g) g.setAttribute("transform", "translate(" + dx + ",0)");
    if (h) h.setAttribute("transform", "translate(" + dx + ",0)");
  }

  private scrollToY(y: number): void {
    const s = this.scroller;
    const target = Math.max(0, Math.min(y * this.S.zoom - 56, s.scrollHeight - s.clientHeight));
    const start = s.scrollTop;
    const d = target - start;
    cancelAnimationFrame(this.scrollRAF);
    if (this.reduced.matches || Math.abs(d) < 3) {
      s.scrollTop = target;
      return;
    }
    const dur = Math.min(680, 260 + Math.abs(d) * 0.22);
    const t0 = performance.now();
    let arrived = false;
    const step = (now: number): void => {
      const k = Math.min(1, (now - t0) / dur);
      const e = k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2;
      s.scrollTop = start + d * e;
      if (k < 1) this.scrollRAF = requestAnimationFrame(step);
      else arrived = true;
    };
    this.scrollRAF = requestAnimationFrame(step);
    setTimeout(() => {
      if (!arrived) s.scrollTop = target;
    }, dur + 140);
  }

  private setZoom(z: number): void {
    this.S.zoom = Math.min(2.2, Math.max(0.45, +z.toFixed(2)));
    this.draw();
  }

  private setPanel(open: boolean, moveFocus?: boolean): void {
    this.$("#main").classList.toggle("pcoll", !open);
    this.$("#panel").setAttribute("aria-hidden", String(!open));
    this.updateLegend();
    if (moveFocus !== false) (open ? this.$("#panelClose") : this.$("#panelOpen")).focus();
  }

  private updateLegend(): void {
    const open = !this.$("#main").classList.contains("pcoll");
    const leg = this.root.querySelector("#legend") as HTMLElement | null;
    if (leg) leg.hidden = !(open && !this.S.sel);
  }

  /** allinea etichetta e stato del bottone tema del guscio */
  private paintTheme(): void {
    const btn = this.$("#theme");
    btn.textContent = this.S.theme === "dark" ? "☀ Chiaro" : "☾ Scuro";
    btn.setAttribute("aria-pressed", String(this.S.theme === "light"));
  }

  /** cambia tema: colori del diagramma, chip degli attori, dettaglio, memoria */
  private setTheme(theme: "dark" | "light"): void {
    this.S.theme = theme;
    applyTheme(theme);
    this.draw();
    this.root.querySelectorAll<HTMLElement>("#fActors button").forEach((b) => {
      const p = this.model.partById.get(b.dataset.actor!);
      const chip = b.querySelector(".chip") as HTMLElement | null;
      if (p && chip) chip.style.color = p.colors[theme];
    });
    this.paintTheme();
    this.renderDetail();
  }

  /* ---- eventi ---- */
  private wire(): void {
    this.$("#mDim").addEventListener("click", () => {
      this.S.mode = "dim";
      this.draw();
    });
    this.$("#mHide").addEventListener("click", () => {
      this.S.mode = "hide";
      this.draw();
    });
    // il guscio ha l'etichetta statica "☀ Chiaro": se si arriva col tema chiaro
    // già attivo (ricordato dalla home) va allineata subito
    this.paintTheme();
    this.$("#theme").addEventListener("click", () => {
      this.setTheme(this.S.theme === "dark" ? "light" : "dark");
    });
    // il tema si può cambiare anche dalla barra dell'app, fuori dal guscio
    document.addEventListener("fullsteps:theme", () => {
      const wanted = savedTheme();
      if (wanted !== this.S.theme) this.setTheme(wanted);
    });
    this.$("#panelClose").addEventListener("click", () => this.setPanel(false));
    this.$("#panelOpen").addEventListener("click", () => this.setPanel(true));
    this.$("#zIn").addEventListener("click", () => this.setZoom(this.S.zoom + 0.12));
    this.$("#zOut").addEventListener("click", () => this.setZoom(this.S.zoom - 0.12));
    this.$("#zRst").addEventListener("click", () => this.setZoom(1));
    let t: ReturnType<typeof setTimeout> | null = null;
    this.$("#q").addEventListener("input", (e) => {
      if (t) clearTimeout(t);
      t = setTimeout(() => {
        this.S.q = (e.target as HTMLInputElement).value;
        this.draw();
      }, 130);
    });
    this.$("#foldAll").addEventListener("click", () => {
      if (this.S.collapsed.size === this.model.PHASES.length) this.S.collapsed.clear();
      else for (const p of this.model.PHASES) this.S.collapsed.add(p.id);
      this.draw();
    });
    this.$("#reset").addEventListener("click", () => {
      this.S.actors.clear();
      this.S.phases.clear();
      this.S.docs.clear();
      this.S.collapsed.clear();
      this.S.q = "";
      (this.$("#q") as HTMLInputElement).value = "";
      this.S.mode = "dim";
      this.S.zoom = 1;
      this.S.sel = null;
      this.draw();
      this.renderDetail();
      this.renderDocSummary();
      this.scroller.scrollTo({ top: 0, left: 0 });
    });
    this.scroller.addEventListener("scroll", () => this.pinGutter(), { passive: true });
    this.scroller.addEventListener(
      "wheel",
      (e) => {
        if (!e.ctrlKey && !e.metaKey) return;
        e.preventDefault();
        this.setZoom(this.S.zoom + (e.deltaY < 0 ? 0.1 : -0.1));
      },
      { passive: false },
    );
    this.diag.addEventListener("keydown", (e) => {
      const ke = e as KeyboardEvent;
      if (ke.key !== "ArrowDown" && ke.key !== "ArrowUp") return;
      const all = [...this.diag.querySelectorAll<SVGElement>("g.msg")];
      const i = all.indexOf(document.activeElement as unknown as SVGElement);
      if (i === -1) return;
      ke.preventDefault();
      const nxt = all[i + (ke.key === "ArrowDown" ? 1 : -1)];
      if (nxt) (nxt as unknown as HTMLElement).focus();
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.S.sel) {
        this.S.sel = null;
        this.draw();
        this.renderDetail();
      }
    });
  }
}
